import React, { useMemo } from "react";

import { Path } from "react-hook-form";

import styled from "@emotion/styled";
import shallow from "zustand/shallow";

import { navigate } from "../../../../hooks/navigation";
import { QuestionnaireHooks } from "../../../../stores/questionnaire";
import Headline from "../../../base/atoms/Headline";
import Container from "../../../shared/Container";
import ReactHookInput from "../../../shared/forms/ReactHookInput";
import { ErrorWrapper } from "../../../shared/forms/ReactHookInput/ErrorMessage";
import Button from "../../../shared/NewForms/Button";

const ButtonWrapper = styled.div`
    display: flex;
    flex-direction: row;
    column-gap: 20px;
    align-items: center;
    justify-content: space-between;
    margin-top: 50px;
    margin-bottom: 20px;

    & > button {
        max-width: 400px;
    }
`;

export type InquiryQuestionnaireProps<
    questionnaireFormState extends Record<string, string>
> = {
    questionnaireHooks: QuestionnaireHooks<questionnaireFormState>;
    summaryPath: string;
    contactPath: string;
};

const InquiryQuestionnaire = <
    questionnaireFormState extends Record<string, string>
>({
    questionnaireHooks: { useQuestionnaireStore, useInquiryForm },
    summaryPath,
    contactPath,
}: InquiryQuestionnaireProps<questionnaireFormState>) => {
    const {
        currentQuestionnaireStep,
        prevQuestionnaireStep,
        nextQuestionnaireStep,
        pages,
    } = useQuestionnaireStore(
        ({
            currentQuestionnaireStep,
            prevQuestionnaireStep,
            nextQuestionnaireStep,
            pages,
        }) => ({
            currentQuestionnaireStep,
            prevQuestionnaireStep,
            nextQuestionnaireStep,
            pages,
        }),
        shallow
    );

    const currentPage = useMemo(
        () => pages[currentQuestionnaireStep],
        [pages, currentQuestionnaireStep]
    );

    const {
        control,
        formState: { errors },
        handleSubmit,
        register,
    } = useInquiryForm();

    const goToNext = () => {
        if (currentQuestionnaireStep === pages.length - 1) {
            void navigate(summaryPath);
        } else {
            nextQuestionnaireStep();
        }
    };

    const goToPrevious = () => {
        if (currentQuestionnaireStep === 0) {
            void navigate(contactPath);
        } else {
            prevQuestionnaireStep();
        }
    };

    if (!currentPage) return null;

    return (
        <>
            <form onSubmit={handleSubmit(goToNext)}>
                <Headline level={2}>{currentPage.headline}</Headline>

                <Container>
                    {currentPage.questions.map(
                        (question, qi) =>
                            question && (
                                <div key={qi} style={{ marginBottom: "30px" }}>
                                    <p>{question.text}</p>

                                    {question.type === "options" ? (
                                        <>
                                            {question.options.map((o, oi) => (
                                                <div key={oi}>
                                                    <label>
                                                        <input
                                                            key={`${currentQuestionnaireStep}-${qi}-${oi}`}
                                                            {...register(
                                                                question.name as Path<questionnaireFormState>,
                                                                {
                                                                    required:
                                                                        true,
                                                                }
                                                            )}
                                                            style={{
                                                                accentColor:
                                                                    "darkblue",
                                                            }}
                                                            type="radio"
                                                            value={o.value}
                                                        />
                                                        <span
                                                            style={{
                                                                marginLeft:
                                                                    "10px",
                                                            }}
                                                        >
                                                            {o.text}
                                                        </span>
                                                    </label>
                                                </div>
                                            ))}

                                            {errors[question.name]?.type ===
                                                "required" && (
                                                <ErrorWrapper>
                                                    Bitte wählen sie eine Option
                                                    aus.
                                                </ErrorWrapper>
                                            )}
                                        </>
                                    ) : question.type === "text" ? (
                                        <ReactHookInput
                                            key={`${currentQuestionnaireStep}-${qi}`}
                                            name={
                                                question.name as Path<questionnaireFormState>
                                            }
                                            control={control}
                                            placeholder={
                                                question.placeholder ?? ""
                                            }
                                            rules={{
                                                required: true,
                                            }}
                                        />
                                    ) : null}
                                </div>
                            )
                    )}

                    <ButtonWrapper>
                        <Button
                            color="blueOutline"
                            type="button"
                            onClick={goToPrevious}
                        >
                            Zurück
                        </Button>

                        <Button
                            color="blue"
                            type="submit"
                            disabled={currentPage.questions.length === 0}
                        >
                            Weiter
                        </Button>
                    </ButtonWrapper>
                </Container>
            </form>
        </>
    );
};

export default InquiryQuestionnaire;
